import { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('portal_token');
    const savedUser = localStorage.getItem('portal_user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));

      // Validate the restored token against the canonical endpoint.
      //
      // Why this matters: rehydrating from localStorage alone makes an EXPIRED
      // token look perfectly logged in — full UI, your name, everything — while
      // every write silently 401s. (That's how a completed 1:1 was lost with no
      // error.) The axios interceptor only ends a session on a /auth/me 401, and
      // until now NOTHING ever called /auth/me, so a dead session never ended.
      //
      // A 401 here is the one authoritative "token is dead" signal: the
      // interceptor clears storage + fires portal:session-expired, the listener
      // below soft-logs-out, and ProtectedRoute redirects to /login (which then
      // returns you to the page you wanted). Non-401 failures (offline, server
      // blip) are deliberately ignored so a flaky network can never log anyone
      // out — that's what used to kick teachers off the clock page.
      //
      // Mount-only on purpose: a periodic re-check could log someone out midway
      // through filling a long form and destroy their unsaved work.
      api.get('/auth/me')
        .then(({ data }) => {
          const fresh = { ...JSON.parse(savedUser), ...data };
          localStorage.setItem('portal_user', JSON.stringify(fresh));
          setUser(fresh);
        })
        .catch(() => { /* 401 → handled by interceptor; anything else ignored */ });
    }
    setLoading(false);
  }, []);

  // Listen for the "session-expired" signal from the axios interceptor.
  // Only fires on a real /auth/me 401 — flaky background polls no
  // longer trip this. When it fires, we soft-logout via React state
  // and ProtectedRoute redirects to /login on the next render.
  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('portal:session-expired', onExpired);
    return () => window.removeEventListener('portal:session-expired', onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('portal_token', data.token);
    localStorage.setItem('portal_user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  // Used by the password-setup flow to drop a freshly-issued JWT into
  // the same place /login would, so the candidate lands logged-in.
  const setSession = (newToken, newUser) => {
    localStorage.setItem('portal_token', newToken);
    localStorage.setItem('portal_user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedFields) => {
    const newUser = { ...user, ...updatedFields };
    localStorage.setItem('portal_user', JSON.stringify(newUser));
    setUser(newUser);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      const updatedUser = { ...user, ...data };
      localStorage.setItem('portal_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const isAuthenticated = !!token;
  const isDirector = user?.role === 'director';
  const isAdmin = user?.role === 'admin';
  const isManagement = isDirector || isAdmin;
  const isTeacher = user?.role === 'teacher';
  const isFloat = user?.role === 'float';
  const isCook = user?.role === 'cook';

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      isAuthenticated, isDirector, isAdmin, isManagement, isTeacher, isFloat, isCook,
      login, logout, updateUser, refreshUser, setSession
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
